"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import type { LogisticsMilestone } from "@/lib/sse/useSSE";
import { CorridorMap } from "@/components/scenes/shared/CorridorMap";

/**
 * Screen 2.5 — SmartLogistics Live. Stylised corridor map of the vessel's
 * progress, ETA, cold-chain telemetry, schedule reliability. As milestones
 * arrive (Loading → Departure → Arrival), the Trace pillar fills.
 */

type MilestoneKind = "Loading" | "Departure" | "Arrival";
type MilestoneRow = { milestone: MilestoneKind; portKey: string };

const MILESTONES_BR_TO_AE: MilestoneRow[] = [
  { milestone: "Loading",   portKey: "portSantosBr" },
  { milestone: "Departure", portKey: "portSantosBr" },
  { milestone: "Arrival",   portKey: "portJebelAliAe" },
];

const MILESTONES_AE_TO_BR: MilestoneRow[] = [
  { milestone: "Loading",   portKey: "portAbuDhabiAe" },
  { milestone: "Departure", portKey: "portAbuDhabiAe" },
  { milestone: "Arrival",   portKey: "portItaquiBr" },
];

// T4 air-cargo BR → UAE (Viracopos → Doha → Dubai). Three-leg in reality
// but condensed to Loading / Departure / Arrival for the same milestone
// shape T1/T2/T3 use.
const MILESTONES_AIR_BR_TO_AE: MilestoneRow[] = [
  { milestone: "Loading",   portKey: "portViracoposBr" },
  { milestone: "Departure", portKey: "portViracoposBr" },
  { milestone: "Arrival",   portKey: "portDubaiCargoAe" },
];

const MILESTONE_LABEL_KEYS: Record<MilestoneKind, string> = {
  Loading:   "milestoneLoading",
  Departure: "milestoneDeparture",
  Arrival:   "milestoneArrival",
};

export function SmartLogistics({
  milestones,
  direction = "br-to-ae",
  modality = "sea",
  carrierName,
  rightColumnExtra,
  className,
}: {
  milestones: LogisticsMilestone[];
  direction?: "br-to-ae" | "ae-to-br";
  /** Trade modality. "sea" (default) shows vessel-based corridor + cold
   *  chain. "air" swaps to airport codes (VCP / DOH / DXB), an ✈ glyph
   *  badge, and shorter ETA copy. T4 7.9 sets modality="air". */
  modality?: "sea" | "air";
  /** Carrier brand label shown in the SmartLogistics subtitle. Defaults
   *  by direction × modality if omitted (Maersk on br-to-ae sea, etc.).
   *  T4 passes "Qatar Airways Cargo". */
  carrierName?: string;
  /** Optional card to render below the ETA card in the right column.
   *  Used to mount the crane illustration on T2 4.14 + T3 5.9 without
   *  overlapping the corridor map. */
  rightColumnExtra?: React.ReactNode;
  className?: string;
}) {
  const t = useTranslations("smartLogistics");
  const reached = new Set(milestones.map((m) => m.milestone));
  const lastMilestone = milestones[milestones.length - 1];
  const tempC = lastMilestone?.tempC ?? -18;
  const milestoneRows = modality === "air"
    ? MILESTONES_AIR_BR_TO_AE
    : direction === "ae-to-br" ? MILESTONES_AE_TO_BR : MILESTONES_BR_TO_AE;
  const progress = reached.has("Arrival")
    ? 1
    : reached.has("Departure")
      ? 0.55
      : reached.has("Loading")
        ? 0.08
        : 0;
  const headerTitle = modality === "air"
    ? t("headerAirBrToAe")
    : direction === "ae-to-br" ? t("headerAeToBr") : t("headerBrToAe");
  const defaultCarrier = modality === "air"
    ? t("carrierAir")
    : direction === "ae-to-br" ? t("carrierAeToBr") : t("carrierBrToAe");
  const carrierLabel = carrierName ?? defaultCarrier;
  const vesselOrFlight = modality === "air"
    ? t("vesselAir")
    : direction === "ae-to-br" ? t("vesselAeToBr") : t("vesselBrToAe");

  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("kickerPrefix")} {carrierLabel}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{headerTitle}</h3>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-orbitra-teal-soft">
            <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-2 py-0.5 font-mono">
              {vesselOrFlight}
            </span>
            <span className="rounded-full border border-policy-pass/40 bg-policy-pass/10 px-2 py-0.5 font-mono text-policy-pass">
              {t("onTimeScore")}
            </span>
          </div>
        </div>

        <div className="mt-4 flex-1 min-h-[180px]">
          <CorridorMap direction={direction} progress={progress} reached={reached} modality={modality} />
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
          {milestoneRows.map((m) => {
            const hit = reached.has(m.milestone);
            return (
              <div
                key={m.milestone}
                className={clsx(
                  "flex flex-col rounded-md border px-3 py-2",
                  hit
                    ? "border-state-traced/50 bg-state-traced/10 text-state-traced shadow-[0_0_18px_var(--state-traced-glow)]"
                    : "border-orbitra-muted/20 bg-orbitra-ink/30 text-orbitra-muted",
                )}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider">{t(MILESTONE_LABEL_KEYS[m.milestone])}</span>
                <span className="text-[10px]">{t(m.portKey)}</span>
                {hit && <span className="mt-1 text-[10px]">{t("recordedTag")}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/* Cold-chain telemetry — T1/T2 frozen poultry + T4 chilled meat
         *  by air. T3 carries dry urea bulk, no cold-chain. Driven by
         *  direction (sea br-to-ae) OR modality (air, always chilled
         *  for the meat use-case). */}
        {(direction === "br-to-ae" || modality === "air") && (
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("coldChainTitle")}</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-3xl font-semibold text-state-traced">{tempC}°C</span>
              <span className="pb-1 text-[10px] text-orbitra-teal-soft">{t("coldChainTarget")}</span>
            </div>
            <p className="mt-2 text-[10px] text-orbitra-muted">{t("samplingNote")}</p>
            <div className="mt-3 grid grid-cols-3 gap-1 text-[9px]">
              {Array.from({ length: 12 }).map((_, i) => (
                <span
                  key={i}
                  className={clsx(
                    "h-1.5 rounded-full",
                    i < milestones.length * 4 ? "bg-state-traced" : "bg-orbitra-muted/20",
                  )}
                />
              ))}
            </div>
          </div>
        )}

        {/* ESG carbon emissions — rendered for BOTH corridors. T3 had it
         *  originally; T1/T2 now show it under the cold-chain telemetry per
         *  the latest amendment. */}
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("esgCarbonTitle")}</p>
          <div className="mt-3 flex items-end gap-2">
            <span className="font-display text-3xl font-semibold text-state-traced">462 t</span>
            <span className="pb-1 text-[10px] text-orbitra-teal-soft">{t("co2eLabel")}</span>
          </div>
          <p className="mt-2 text-[10px] text-orbitra-muted">{t("esgIntensity")}</p>
          <p className="text-[10px] text-orbitra-muted">{t("esgLedgerNote")}</p>
          <div className="mt-3 grid grid-cols-3 gap-1 text-[9px]">
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className={clsx(
                  "h-1.5 rounded-full",
                  i < milestones.length * 4 ? "bg-state-traced" : "bg-orbitra-muted/20",
                )}
              />
            ))}
          </div>
        </div>
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("etaLabel")}</p>
          <p className="mt-1 font-display text-base font-semibold text-orbitra-paper">
            {modality === "air" ? t("etaAir") : t("etaSea")}
          </p>
          <p className="mt-1 text-[10px] text-orbitra-muted">
            {modality === "air" ? t("etaNoteAir") : t("etaNoteSea")}
          </p>
        </div>
        {rightColumnExtra}
      </div>
    </div>
  );
}
